// App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser, isAuthenticated, validateToken } from './services/api';
import { CartProvider } from './context/CartContext';
import Header from './components/Header';
import HomePage from './pages/Home';
import './styles/loadingscreen.css';
import Dashboard from './pages/Dashboard';
import ServicesPage from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import BookingPage from './pages/Booking';
import BookingSuccessPage from './pages/BookingSuccess';
import './styles/globals.css';
import './styles/components.css';
import './styles/mobile-responsive.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (isAuthenticated()) {
          // Get user from localStorage
          const currentUser = getCurrentUser();
          
          if (currentUser) {
            // Optionally validate token with backend
            try {
              const validation = await validateToken();
              if (validation.success && validation.valid) {
                setUser(validation.user);
              } else {
                // Token is invalid, clear storage
                localStorage.removeItem('user');
                localStorage.removeItem('jwtToken');
                setUser(null);
              }
            } catch (error) {
              console.error('Token validation error:', error);
              // On validation error, still use stored user but mark as needing refresh
              setUser(currentUser);
            }
          } else {
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleLogout = () => {
    setUser(null);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="logo-container">
            <img 
              src="/logo.png" 
              alt="EaseMyHomeServices Logo" 
              className="loading-logo"
            />
          </div>
          <div className="loading-spinner"></div>
          <h2 className="loading-title">EaseMyHomeServices</h2>
          <p className="loading-text">Loading your home service experience...</p>
        </div>
      </div>
    );
  }

  return (
    <CartProvider>
      <Router>
        <div className="App">
          {/* Always show Header except on dashboard */}
          <Routes>
            <Route 
              path="/dashboard" 
              element={
                user ? (
                  <Dashboard user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            <Route 
              path="*" 
              element={
                <>
                  <Header user={user} onLogout={handleLogout} />
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <HomePage user={user} onLoginSuccess={handleLoginSuccess} />
                      } 
                    />
                    <Route 
                      path="/home" 
                      element={
                        <HomePage user={user} onLoginSuccess={handleLoginSuccess} />
                      } 
                    />
                    <Route 
                      path="/services" 
                      element={
                        <ServicesPage user={user} onLoginSuccess={handleLoginSuccess} />
                      } 
                    />
                    <Route 
                      path="/service/:serviceName" 
                      element={
                        <ServiceDetail user={user} />
                      } 
                    />
                    <Route 
                      path="/booking" 
                      element={
                        user ? (
                          <BookingPage user={user} />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      } 
                    />
                    <Route 
                      path="/booking-success" 
                      element={
                        user ? (
                          <BookingSuccessPage />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      } 
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </>
              } 
            />
          </Routes>
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;